import React, { useMemo, useState } from 'react';
import DataTable from '../../../components/ui/DataTable/DataTable';
import TableToolbar from '../../../components/ui/DataTable/TableToolbar';
import RowActionsMenu from '../../../components/ui/DataTable/RowActionsMenu';
import StatusPill from '../../../components/ui/StatusPill/StatusPill';
import { Badge } from '../../../components/ui/Badge';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../../../components/ui/Select';
import styles from './DocumentAssessment.module.css';

/**
 * The gap list — the product of an assessment.
 *
 * Columns are the four questions a gap must answer, in the order a customer
 * asks them: how bad, what is missing, which clause an assessor would cite,
 * where in their own document. The clause column is what makes a row defensible
 * rather than an opinion.
 *
 * The server ranks by severity; this table does not re-sort by default, because
 * severity here means "how likely an assessor is to write this up".
 */

const SEVERITY_TONE = { high: 'error', medium: 'warning', low: 'info' };
const STATE_TONE = { open: 'warning', resolved: 'success', dismissed: 'neutral' };
const LEVEL_LABEL = {
  work_product: 'Work product',
  content: 'Content',
  linkage: 'Linkage',
};

export const FILTERABLE = ['severity', 'level', 'state'];

const GapsTable = ({ gaps = [], onOpen, onDismiss, onReopen, onAssign, loading }) => {
  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState('');

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return gaps.filter((g) => {
      for (const key of FILTERABLE) {
        const wanted = filters[key];
        if (wanted?.length && !wanted.includes(g[key])) return false;
      }
      if (!term) return true;
      return (
        (g.title || '').toLowerCase().includes(term) ||
        (g.iso_clause || '').toLowerCase().includes(term) ||
        (g.detail || '').toLowerCase().includes(term)
      );
    });
  }, [gaps, filters, search]);

  const columns = [
    {
      key: 'severity',
      label: 'Severity',
      width: '10%',
      render: (row) => (
        <StatusPill
          tone={SEVERITY_TONE[row.severity] || 'neutral'}
          label={row.severity}
          size="sm"
        />
      ),
    },
    {
      key: 'title',
      label: 'Gap',
      width: '34%',
      render: (row) => (
        <div>
          <div className={styles.gapTitle}>{row.title}</div>
          {row.affected_count > 0 && (
            <div className={styles.gapCounts}>
              {row.affected_count} of {row.total_count} record(s)
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'iso_clause',
      label: 'ISO clause',
      width: '10%',
      render: (row) => (
        <Badge variant="outline" color="gray" size="1" radius="small">
          {row.iso_clause || '—'}
        </Badge>
      ),
    },
    {
      key: 'level',
      label: 'Level',
      width: '11%',
      render: (row) => LEVEL_LABEL[row.level] || row.level,
    },
    {
      key: 'where',
      label: 'Where',
      width: '21%',
      render: (row) =>
        row.source?.document_name ? (
          <span>
            {row.source.document_name}
            {row.source.location ? ` — ${row.source.location}` : ''}
          </span>
        ) : (
          <span className={styles.notFound}>not found in any document</span>
        ),
    },
    {
      key: 'state',
      label: 'Status',
      width: '9%',
      render: (row) => (
        <StatusPill tone={STATE_TONE[row.state] || 'neutral'} label={row.state} size="sm" />
      ),
    },
    {
      key: '_actions',
      label: '',
      align: 'right',
      width: '5%',
      render: (row) => (
        <RowActionsMenu
          ariaLabel={`Actions for ${row.title}`}
          actions={[
            { id: 'open', label: 'View detail', onClick: () => onOpen?.(row) },
            ...(row.state === 'dismissed'
              ? [{ id: 'reopen', label: 'Reopen', onClick: () => onReopen?.(row) }]
              : [
                  { id: 'assign', label: 'Assign…', onClick: () => onAssign?.(row) },
                  {
                    id: 'dismiss',
                    label: 'Dismiss…',
                    danger: true,
                    onClick: () => onDismiss?.(row),
                  },
                ]),
          ]}
        />
      ),
    },
  ];

  // `all` rather than '' because SelectValue falls back to its placeholder on
  // an empty value, which would leave the trigger looking unset while a filter
  // is in fact cleared.
  const setFilter = (key) => (value) =>
    setFilters((f) => ({ ...f, [key]: value === 'all' ? undefined : [value] }));

  const filterSelects = (
    <>
      <Select value={filters.severity?.[0] || 'all'} onValueChange={setFilter('severity')}>
        <SelectTrigger className={styles.filterSelect} aria-label="Filter by severity">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All severities</SelectItem>
          <SelectItem value="high">High</SelectItem>
          <SelectItem value="medium">Medium</SelectItem>
          <SelectItem value="low">Low</SelectItem>
        </SelectContent>
      </Select>
      <Select value={filters.state?.[0] || 'all'} onValueChange={setFilter('state')}>
        <SelectTrigger className={styles.filterSelect} aria-label="Filter by status">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All statuses</SelectItem>
          <SelectItem value="open">Open</SelectItem>
          <SelectItem value="resolved">Resolved</SelectItem>
          <SelectItem value="dismissed">Dismissed</SelectItem>
        </SelectContent>
      </Select>
    </>
  );

  return (
    <DataTable
      columns={columns}
      data={rows}
      rowKey="gap_id"
      loading={loading}
      onRowClick={onOpen}
      emptyMessage="No gaps match these filters."
      toolbar={
        <TableToolbar
          showFilterButton={false}
          search={{
            value: search,
            onChange: setSearch,
            placeholder: 'Search gaps or clauses',
          }}
          count={{ value: rows.length, label: 'shown' }}
          leadingActions={filterSelects}
        />
      }
    />
  );
};

export default GapsTable;
